/**
 * 学生端「奖品屋」（见契约 2.2）。
 *
 * - 奖品列表 + 兑换按钮；**余额不足时禁用并说明差多少**（"还差 X 分"）；
 * - 我的兑换记录（待审批 / 已批准 / 已发放 / 被拒绝）；
 * - 儿童模式：大按钮、图标+文字、兑换流程尽量短（点奖品 → 确认，共 2 步）。
 *
 * 防超兑（方案 3.4.2 + 契约 5「连点两次兑换不会超兑」）：
 * 兑换请求带 `Idempotency-Key`，**一次用户意图一个键**；同时按钮在请求进行中禁用，
 * 双保险 —— 幂等键防"网络重试重复下单"，禁用防"手抖连点"。
 *
 * id 空间：studentId 必须是 **student.id**（见 lib/useMyStudent.ts）。
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShell } from '../../components/layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import { ErrorState, NotReadyState } from '../../components/ui/Bits';
import {
  usePointsSummary,
  useRedemptionMutations,
  useRedemptions,
  useStudentRewardList,
} from '../../lib/domainQueries';
import { useMyStudent } from '../../lib/useMyStudent';
import { resolveUiMode } from '../../lib/uiMode';
import { newIdempotencyKey } from '../../lib/api/client';
import { toErrorMessage } from '../../lib/api/error';
import { cn, formatDateTimeFull } from '../../lib/utils';
import type { Redemption, Reward } from '../../lib/api/types';

const STATUS_META: Record<
  Redemption['status'],
  { label: string; tone: 'warn' | 'success' | 'danger' | 'neutral'; icon: string; hint: string }
> = {
  pending: { label: '等家长确认', tone: 'warn', icon: '⏳', hint: '家长同意后才会扣分' },
  approved: { label: '已通过', tone: 'success', icon: '✅', hint: '积分已扣除，等家长发放奖品' },
  fulfilled: { label: '已拿到', tone: 'success', icon: '🎉', hint: '奖品已经给你了' },
  rejected: { label: '没通过', tone: 'danger', icon: '🚫', hint: '积分已经退回给你' },
  cancelled: { label: '已取消', tone: 'neutral', icon: '↩️', hint: '这次兑换取消了' },
};

export function StudentRewardsPage() {
  const my = useMyStudent();

  if (my.isLoading) {
    return (
      <AppShell kind="student" title="奖品屋">
        <Skeleton className="h-40 w-full" />
      </AppShell>
    );
  }
  if (!my.studentId) {
    return (
      <AppShell kind="student" title="奖品屋">
        <ErrorState
          message={toErrorMessage(my.error) || '没能确认你的学生档案，请稍后重试'}
          onRetry={my.refetch}
        />
      </AppShell>
    );
  }
  return <StudentRewardsScreen studentId={my.studentId} />;
}

function StudentRewardsScreen({
  studentId,
}: {
  studentId: string;
}) {
  const toast = useToast();
  const rewardsQuery = useStudentRewardList(studentId);
  const pointsQuery = usePointsSummary(studentId);
  const redemptionsQuery = useRedemptions(studentId);
  const mutations = useRedemptionMutations(studentId);

  const [confirming, setConfirming] = useState<Reward | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** 一次兑换意图一个幂等键；确认弹窗打开时生成，重试复用。 */
  const [idempotencyKey, setIdempotencyKey] = useState('');

  /** 奖品屋接口不返回 `uiMode`（只有今日打卡接口返回），所以来源只有它一个。 */
  const mode = resolveUiMode({ serverUiMode: null });
  const kid = mode === 'child';

  const balance = pointsQuery.data?.balance ?? 0;
  const rewards = (rewardsQuery.data?.rewards ?? []).filter((reward) => reward.active);
  const redemptions = redemptionsQuery.data?.redemptions ?? [];
  const notReady = rewardsQuery.data?.notReady ?? false;

  const openConfirm = (reward: Reward) => {
    setConfirming(reward);
    setNote('');
    setError(null);
    // 幂等键在这里生成：同一份"我想换这个"的意图在重试时沿用同一个键
    setIdempotencyKey(newIdempotencyKey(`redeem:${studentId}:${reward.id}`));
  };

  const submit = async () => {
    if (!confirming) return;
    setError(null);
    try {
      await mutations.request.mutateAsync({
        studentId,
        rewardId: confirming.id,
        note: note.trim(),
        idempotencyKey,
      });
      toast.showToast(
        confirming.needApproval ? '已经告诉家长啦，等他确认' : '兑换成功！积分已扣除',
        'success',
      );
      setConfirming(null);
    } catch (err) {
      setError(toErrorMessage(err));
    }
  };

  return (
    <AppShell
      kind="student"
      title={kid ? '我能换什么？' : '奖品屋'}
      compactHeader={kid}
      headerAction={
        <span
          className={cn(
            'rounded-xl bg-amber-50 font-bold text-amber-700',
            kid ? 'px-3 py-2 text-lg' : 'px-3 py-1.5 text-sm',
          )}
          aria-label={`我的积分 ${balance}`}
        >
          ⭐ {balance}
        </span>
      }
    >
      <div className={cn('space-y-5', kid && 'ui-child space-y-6')}>
        {/* ---------- 奖品列表 ---------- */}
        <Card className={kid ? 'p-5' : undefined}>
          <CardHeader
            title={kid ? '奖品在这里' : '可以兑换的奖品'}
            description="积分够了就能换；需要家长同意的，会先发给家长确认。"
          />

          {rewardsQuery.isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          ) : rewardsQuery.isError ? (
            <ErrorState
              message={toErrorMessage(rewardsQuery.error)}
              onRetry={() => void rewardsQuery.refetch()}
              compact
            />
          ) : notReady ? (
            <NotReadyState
              feature="奖品屋"
              description="奖品接口还在上线中。家长添加奖品后，这里会立刻显示可以换的东西。"
            />
          ) : rewards.length === 0 ? (
            <EmptyState
              icon="🎁"
              title="奖品屋还在准备中"
              description="等家长添加奖品后，这里就会出现可以兑换的东西。可以先继续打卡攒积分。"
            />
          ) : (
            <ul className="space-y-3">
              {rewards.map((reward) => {
                const affordable = balance >= reward.costPoints;
                const soldOut = reward.stock !== null && reward.stock <= 0;
                const short = reward.costPoints - balance;
                return (
                  <li
                    key={reward.id}
                    className={cn(
                      'flex flex-wrap items-center gap-3 rounded-2xl border bg-white sm:flex-nowrap',
                      kid ? 'border-2 p-4' : 'border-slate-200 p-3',
                      (!affordable || soldOut) && 'opacity-70',
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'flex shrink-0 items-center justify-center rounded-2xl bg-amber-50',
                        kid ? 'h-16 w-16 text-4xl' : 'h-12 w-12 text-2xl',
                      )}
                    >
                      {reward.imageUrl ? '🖼️' : '🎁'}
                    </span>

                    <div className="min-w-0 flex-1">
                      <p className={cn('font-semibold text-slate-900', kid ? 'text-xl' : 'text-base')}>
                        {reward.name}
                      </p>
                      <p className={cn('text-slate-500', kid ? 'text-base' : 'text-sm')}>
                        {reward.costPoints} 积分
                        {reward.stock !== null && ` · 还剩 ${reward.stock} 个`}
                        {reward.needApproval && ' · 需要家长同意'}
                      </p>
                      {/* 差多少分 —— 激励的关键，必须说清楚 */}
                      {!affordable && !soldOut && (
                        <p className={cn('mt-0.5 font-medium text-amber-600', kid ? 'text-base' : 'text-xs')}>
                          还差 {short} 分
                        </p>
                      )}
                    </div>

                    <Button
                      size={kid ? 'kid' : 'sm'}
                      variant={affordable && !soldOut ? (kid ? 'kid-green' : 'primary') : 'secondary'}
                      disabled={!affordable || soldOut}
                      title={
                        soldOut
                          ? '这个奖品已经兑完了'
                          : affordable
                            ? undefined
                            : `还差 ${short} 分`
                      }
                      onClick={() => openConfirm(reward)}
                    >
                      {soldOut ? '已兑完' : affordable ? '兑换' : `还差 ${short} 分`}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* ---------- 我的兑换记录 ---------- */}
        <Card className={kid ? 'p-5' : undefined}>
          <CardHeader
            title="我的兑换"
            description="家长同意后才会扣分；没通过的话积分会退回给你。"
          />
          {redemptionsQuery.isLoading ? (
            <Skeleton className="h-16 w-full" />
          ) : redemptionsQuery.data?.notReady ? (
            <NotReadyState feature="兑换记录" compact />
          ) : redemptions.length === 0 ? (
            <EmptyState
              icon="🧾"
              title="还没有兑换过东西"
              description="去上面挑一个喜欢的，积分够了就能换。"
            />
          ) : (
            <ul className="divide-y divide-slate-100">
              {redemptions.map((redemption) => {
                const meta = STATUS_META[redemption.status];
                return (
                  <li key={redemption.id} className="flex flex-wrap items-center gap-3 py-3">
                    <span aria-hidden="true" className={kid ? 'text-2xl' : 'text-xl'}>
                      {meta.icon}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={cn('font-medium text-slate-900', kid ? 'text-lg' : 'text-sm')}>
                        {redemption.rewardNameSnapshot}
                      </p>
                      <p className={cn('text-slate-500', kid ? 'text-base' : 'text-xs')}>
                        {meta.hint} · {formatDateTimeFull(redemption.requestedAt)}
                      </p>
                      {redemption.decisionNote && (
                        <p className="text-xs text-slate-500">家长说：{redemption.decisionNote}</p>
                      )}
                    </div>
                    <Badge tone={meta.tone}>{meta.label}</Badge>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        {/* ---------- 规则说明 ---------- */}
        <Card className={kid ? 'p-5' : undefined}>
          <CardHeader title="兑换规则" />
          <ul
            className={cn(
              'list-inside list-disc space-y-1 text-slate-500',
              kid ? 'text-base' : 'text-sm',
            )}
          >
            <li>需要家长同意的奖品，家长同意后才会扣积分。</li>
            <li>如果家长没同意，积分会原样退回给你。</li>
            <li>积分账本只增不改：每一笔加减都能在「我的积分」里看到。</li>
          </ul>
          <div className="mt-3">
            <Link to="/student/points">
              <Button size={kid ? 'kid' : 'sm'} variant="secondary">
                看我的积分
              </Button>
            </Link>
          </div>
        </Card>
      </div>

      {/* ---------- 兑换确认（儿童模式也是 2 步：点兑换 → 确认） ---------- */}
      <Modal
        open={confirming !== null}
        onClose={() => setConfirming(null)}
        title={confirming ? `换「${confirming.name}」？` : ''}
        size="sm"
        footer={
          <div className="flex w-full flex-col gap-2 sm:flex-row sm:gap-3">
            <Button
              variant="secondary"
              size={kid ? 'kid' : 'md'}
              className="sm:flex-1"
              onClick={() => setConfirming(null)}
            >
              再想想
            </Button>
            <Button
              size={kid ? 'kid' : 'md'}
              variant={kid ? 'kid-green' : 'primary'}
              className="sm:flex-1"
              loading={mutations.request.isPending}
              onClick={() => void submit()}
            >
              确认兑换
            </Button>
          </div>
        }
      >
        {confirming && (
          <div className={cn('space-y-3', kid && 'ui-child')}>
            <p className={cn('text-slate-600', kid ? 'text-lg' : 'text-sm')}>
              需要 {confirming.costPoints} 积分。你现在有 {balance} 分，换完还剩{' '}
              <span className="font-semibold">{balance - confirming.costPoints}</span> 分。
            </p>
            {confirming.needApproval && (
              <p className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800">
                这个奖品需要家长同意。你确认后，家长会收到提示；他同意时才会扣积分。
              </p>
            )}
            <Input
              label="想对家长说的话"
              kid={kid}
              maxLength={200}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="例如：这周我很努力，想换这个"
              hint="不想写就留空。"
            />
            {error && (
              <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </p>
            )}
          </div>
        )}
      </Modal>
    </AppShell>
  );
}
